import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { forkJoin, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { Driver, DriverPayload } from '../../core/models/driver.model';
import { Organisation } from '../../core/models/organisation.model';
import { StatusLookup } from '../../core/models/lookup.model';
import { StatusID } from '../../core/enums/status-id.enum';
import { normalizeUserRole, UserRole } from '../../core/enums/user-role.enum';
import { AuthService } from '../../core/services/auth.service';
import { DriverService } from '../../core/services/driver.service';
import { LookupService } from '../../core/services/lookup.service';
import { OrganisationService } from '../../core/services/organisation.service';
import { ConfirmationService } from '../../core/services/confirmation.service';

@Component({
  selector: 'app-driver-management',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './driver-management.component.html',
  styleUrls: ['./management-page.component.css']
})
export class DriverManagementComponent implements OnInit {
  drivers: Driver[] = [];
  organisations: Organisation[] = [];
  statuses: StatusLookup[] = [
    { statusID: StatusID.ACTIVE, statusName: 'Active' },
    { statusID: StatusID.INACTIVE, statusName: 'Inactive' }
  ];
  selectedDriver: Driver | null = null;
  selectedOrganisationId: number | null = null;
  includeInactive = false;
  searchTerm = '';
  showForm = false;
  isEditing = false;
  isLoading = false;
  isSaving = false;
  successMessage = '';
  errorMessage = '';
  usernameError = '';
  linkPassengerProfileId: number | null = null;
  linkError = '';

  driverForm: FormGroup;

  private readonly currentUser;
  readonly currentRole: UserRole;
  readonly isAdminOrPlatform: boolean;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private driverService: DriverService,
    private lookupService: LookupService,
    private organisationService: OrganisationService,
    private confirmations: ConfirmationService
  ) {
    this.currentUser = this.authService.getCurrentUser();
    this.currentRole = normalizeUserRole(
      this.currentUser?.userRoleName || this.currentUser?.userRole || this.currentUser?.userRoleID
    );
    this.isAdminOrPlatform = this.currentRole === UserRole.ADMIN || this.currentRole === UserRole.PLATFORM_EMPLOYEE;

    this.driverForm = this.fb.group({
      userID: [0],
      userPrefix: [''],
      userFirstName: ['', [Validators.required, Validators.maxLength(80)]],
      userLastName: ['', Validators.maxLength(100)],
      username: ['', Validators.maxLength(120)],
      userPassword: [''],
      organisationID: [null, Validators.required],
      statusID: [StatusID.ACTIVE, Validators.required],
      passengerProfileID: [null]
    });
  }

  ngOnInit(): void {
    this.selectedOrganisationId = this.isAdminOrPlatform ? null : this.currentUser?.organisationID || null;
    this.loadInitialData();
  }

  loadInitialData(): void {
    this.isLoading = true;
    forkJoin({
      statuses: this.lookupService.getStatuses().pipe(catchError(() => of(this.statuses))),
      organisations: this.isAdminOrPlatform
        ? this.organisationService.getOrganisations().pipe(catchError(() => of([])))
        : of([]),
      drivers: this.driverService.getDrivers(this.listFilter()).pipe(catchError(() => {
        this.errorMessage = 'Unable to load drivers right now.';
        return of([]);
      }))
    }).pipe(finalize(() => this.isLoading = false)).subscribe(({ statuses, organisations, drivers }) => {
      const driverStatuses = statuses.filter(status => [StatusID.ACTIVE, StatusID.INACTIVE].includes(status.statusID));
      this.statuses = driverStatuses.length ? driverStatuses : this.statuses;
      this.organisations = organisations;
      this.drivers = this.orderDrivers(drivers);
      this.syncSelectedDriver();
    });
  }

  loadDrivers(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.driverService.getDrivers(this.listFilter()).pipe(
      finalize(() => this.isLoading = false)
    ).subscribe({
      next: drivers => {
        this.drivers = this.orderDrivers(drivers);
        this.syncSelectedDriver();
      },
      error: () => this.errorMessage = 'Unable to load drivers right now.'
    });
  }

  startCreate(): void {
    this.showForm = true;
    this.isEditing = false;
    this.usernameError = '';
    this.driverForm.reset({
      userID: 0,
      userPrefix: '',
      userFirstName: '',
      userLastName: '',
      username: '',
      userPassword: '',
      organisationID: this.selectedOrganisationId,
      statusID: StatusID.ACTIVE,
      passengerProfileID: null
    });
    this.setOrganisationControlState(false);
  }

  startEdit(driver: Driver): void {
    this.selectedDriver = driver;
    this.showForm = true;
    this.isEditing = true;
    this.usernameError = '';
    this.driverForm.reset({
      userID: driver.userID,
      userPrefix: driver.userPrefix || '',
      userFirstName: driver.userFirstName || '',
      userLastName: driver.userLastName || '',
      username: driver.username || '',
      userPassword: '',
      organisationID: driver.organisationID,
      statusID: driver.statusID || StatusID.ACTIVE,
      passengerProfileID: driver.passengerProfileID || null
    });
    this.setOrganisationControlState(true);
  }

  saveDriver(): void {
    this.driverForm.markAllAsTouched();
    this.usernameError = '';
    this.errorMessage = '';
    this.successMessage = '';

    if (this.driverForm.invalid) {
      return;
    }

    const raw = this.driverForm.getRawValue();
    const payload: DriverPayload = {
      userID: Number(raw.userID || 0),
      userPrefix: this.nullIfBlank(raw.userPrefix),
      userFirstName: String(raw.userFirstName || '').trim(),
      userLastName: this.nullIfBlank(raw.userLastName),
      username: this.nullIfBlank(raw.username),
      userPassword: this.nullIfBlank(raw.userPassword),
      organisationID: Number(raw.organisationID),
      statusID: Number(raw.statusID),
      passengerProfileID: raw.passengerProfileID ? Number(raw.passengerProfileID) : null
    };

    this.isSaving = true;
    const request = this.isEditing && payload.userID
      ? this.driverService.updateDriver(payload.userID, payload)
      : this.driverService.createDriver(payload);

    request.pipe(finalize(() => this.isSaving = false)).subscribe({
      next: driver => {
        this.successMessage = this.isEditing ? 'Driver updated.' : 'Driver created.';
        this.showForm = false;
        this.loadDrivers();
        this.selectedDriver = driver;
      },
      error: error => {
        if (this.isUsernameConflict(error)) {
          this.usernameError = 'This username is already in use.';
          this.driverForm.get('username')?.setErrors({ conflict: true });
          return;
        }
        this.errorMessage = this.extractError(error, 'Unable to save the driver.');
      }
    });
  }

  async deactivateDriver(driver: Driver): Promise<void> {
    if (!await this.confirmations.confirm({
      title: 'Deactivate driver',
      message: `Deactivate ${this.getDriverName(driver)}? They will not be available for new trip assignment.`,
      confirmLabel: 'Deactivate',
      tone: 'danger'
    })) return;

    this.driverService.deactivateDriver(driver.userID).subscribe({
      next: () => {
        this.successMessage = 'Driver deactivated.';
        this.loadDrivers();
      },
      error: error => this.errorMessage = this.extractError(error, 'Unable to deactivate the driver.')
    });
  }

  selectDriver(driver: Driver): void {
    this.selectedDriver = driver;
    this.linkPassengerProfileId = driver.passengerProfileID || null;
    this.linkError = '';
  }

  linkPassengerProfile(): void {
    if (!this.selectedDriver || !this.linkPassengerProfileId) {
      this.linkError = 'Enter a passenger profile ID to link.';
      return;
    }

    this.linkError = '';
    this.driverService.linkPassengerProfile(this.selectedDriver.userID, Number(this.linkPassengerProfileId)).subscribe({
      next: driver => {
        this.successMessage = 'Passenger profile linked to driver.';
        this.selectedDriver = driver;
        this.loadDrivers();
      },
      error: error => this.linkError = this.extractError(error, 'Unable to link this passenger profile. It may already be linked to a non-driver user.')
    });
  }

  getDriverName(driver: Driver | null): string {
    if (!driver) {
      return '-';
    }

    return [driver.userFirstName, driver.userLastName].filter(Boolean).join(' ') || driver.username || `Driver ${driver.userID}`;
  }

  getOrganisationOptionLabel(organisation: Organisation): string {
    return organisation.organisationName || organisation.orgName || `Organisation ${organisation.organisationID}`;
  }

  isInactive(driver: Driver | null): boolean {
    return Number(driver?.statusID) === StatusID.INACTIVE
      || String(driver?.statusName || '').toLowerCase() === 'inactive';
  }

  isInvalid(controlName: string): boolean {
    const control = this.driverForm.get(controlName);
    return Boolean(control?.invalid && (control.touched || control.dirty));
  }

  hasPassengerProfile(driver: Driver | null): boolean {
    return Boolean(driver?.passengerProfileID);
  }

  private listFilter() {
    return {
      organisationId: this.selectedOrganisationId,
      includeInactive: this.includeInactive,
      search: this.searchTerm
    };
  }

  private setOrganisationControlState(isEdit: boolean): void {
    const control = this.driverForm.get('organisationID');
    if (!this.isAdminOrPlatform || isEdit) {
      control?.disable();
    } else {
      control?.enable();
    }
  }

  private syncSelectedDriver(): void {
    if (!this.selectedDriver) {
      return;
    }

    const match = this.drivers.find(driver => driver.userID === this.selectedDriver?.userID);
    this.selectedDriver = match || null;
  }

  private orderDrivers(drivers: Driver[]): Driver[] {
    return [...drivers].sort((first, second) => this.getDriverName(first).localeCompare(this.getDriverName(second)));
  }

  private nullIfBlank(value: unknown): string | null {
    const text = String(value || '').trim();
    return text || null;
  }

  private isUsernameConflict(error: any): boolean {
    const message = this.extractError(error, '').toLowerCase();
    return error?.status === 409 || message.includes('username') || message.includes('already exists') || message.includes('conflict');
  }

  private extractError(error: any, fallback: string): string {
    return error?.error?.message
      || error?.error?.title
      || (typeof error?.error === 'string' ? error.error : '')
      || error?.message
      || fallback;
  }
}
